import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Modal from '../core/Modal';
import { getNonce, getTables, isPro } from '../Helpers';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { EditIcon, DeleteIcon, Cross, sfText, sfNumber, sfEmail, sfImage, sfTextarea, sfRadio, sfCheckbox, sfSelect, sfCustomCaptcha, sfPagebreak, sfFile, sfCloudflare, sfSign, sfRange, sfWeek, sfUrl, sfLink, sfClock, sfTelephone, sfSubmit, sfBtn, sfpass, sfDate, sfDatetime, sfColor, sfHide, sfHeading, sfLabel, sfVideo, sfAudio, sfiFrame } from '../icons';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import availableFieldsList from './fieldData';
import PreviewModal from './PreviewModal';
import RenderField from './Render';
import presetData from './presetData';
import '../styles/_createForm.scss';
import NoticeModal from '../components/NoticeModal/NoticeModal';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';

interface FormBuilderProps {
    mode: 'create' | 'edit';
}

const FormBuilder: React.FC<FormBuilderProps> = ({ mode }) => {
    const getTable = getTables().length;
    const proData = isPro();
    const allConditionsMet = proData;
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    // Get preset for create mode
    const queryParams = new URLSearchParams(location.search);
    const presetName = queryParams.get('preset');

    const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
    const [showFormFields, setShowFormFields] = useState(true);
    const [totaltable, setTotaltable] = useState(getTable);
    const [availableFields, setAvailableFields] = useState(availableFieldsList);
    const [createTableModal, setCreateTableModal] = useState(false);
    const createTableModalRef = useRef();
    const [formFields, setFormFields] = useState([]);
    const [formData, setFormData] = useState([]);
    const [editingField, setEditingField] = useState(null);
    const [showjson, setShowjson] = useState(false);
    const [editingOptionIndex, setEditingOptionIndex] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isEditingField, setIsEditingField] = useState(false);
    const [showFormSettingsModal, setShowFormSettingsModal] = useState(false);

    // Form data states
    const [formName, setFormName] = useState('');
    const [btnName, setBtnName] = useState('');
    const [formJson, setFormJson] = useState({});
    const [thankyouPageEnabled, setThankyouPageEnabled] = useState(false);
    const [thankyouPageId, setThankyouPageId] = useState(0);
    const [thankyouPageType, setThankyouPageType] = useState('page');
    const [thankyouCustomUrl, setThankyouCustomUrl] = useState('');
    const [thankyouOpenNewTab, setThankyouOpenNewTab] = useState(false);

    // Email notification states
    const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(false);
    const [notificationEmails, setNotificationEmails] = useState(['']);
    const [emailSubject, setEmailSubject] = useState('New Form Submission');
    const [emailMessage, setEmailMessage] = useState('You have received a new form submission.');

    const [wordpressPages, setWordpressPages] = useState([]);

    // Form structure states
    const [formStructure, setFormStructure] = useState([
        { id: 'row-0', type: 'row', fields: [] }
    ]);
    const [activeRow, setActiveRow] = useState(0);
    const [rowLayoutMode, setRowLayoutMode] = useState({});

    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        type: 'confirmation',
        title: '',
        message: '',
        onConfirm: () => { },
        confirmText: 'Confirm',
        declineText: 'Cancel'
    });

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
    };

    const toggleFormFields = () => {
        setShowFormFields(!showFormFields);
    };

    // Load presets for create mode
    useEffect(() => {
        if (mode === 'create' && presetName) {
            const presetFields = presetData[presetName];
            if (presetFields) {
                const initialRow = { id: 'row-0', type: 'row', fields: presetFields };
                setFormStructure([initialRow]);
                setFormFields(presetFields);
            } else {
                console.error(`Preset "${presetName}" not found`);
            }
        }
    }, [presetName, mode]);

    // Load existing form data for edit mode
    useEffect(() => {
        if (mode === 'edit' && id) {
            getTableData();
        }
    }, [mode, id]);

    // Load WordPress pages
    useEffect(() => {
        wp.ajax.send('simpleform_get_pages', {
            data: {
                nonce: getNonce(),
            },
            success(response) {
                setWordpressPages(response.pages);
            },
            error(error) {
                console.error('Error loading pages:', error);
            },
        });
    }, []);

    // Convert formStructure to formFields for backward compatibility
    useEffect(() => {
        const allFields = formStructure.reduce((acc, row) => {
            if (row && row.fields && Array.isArray(row.fields)) {
                return [...acc, ...row.fields];
            }
            return acc;
        }, []);
        setFormFields(allFields);
    }, [formStructure]);

    const getTableData = () => {
        wp.ajax.send('simpleform_edit_table', {
            data: {
                nonce: getNonce(),
                id: id,
            },
            success(response) {
                setFormName(response.form_name);
                setBtnName(response.btn_name);
                setFormJson({ ...response, id: id });

                // Load thank you page settings
                setThankyouPageEnabled(response.thankyou_page_enabled || false);
                setThankyouPageId(response.thankyou_page_id || 0);
                setThankyouPageType(response.thankyou_page_type || 'page');
                setThankyouCustomUrl(response.thankyou_custom_url || '');
                setThankyouOpenNewTab(response.thankyou_open_new_tab || false);

                // Load email notification settings
                setEmailNotificationEnabled(response.email_notification_enabled || false);
                const emails = response.notification_emails ? JSON.parse(response.notification_emails) : [''];
                setNotificationEmails(emails.length > 0 ? emails : ['']);
                setEmailSubject(response.email_subject || 'New Form Submission');
                setEmailMessage(response.email_message || 'You have received a new form submission.');

                // Check if data already has row structure
                if (response.table_settings && Array.isArray(response.table_settings) &&
                    response.table_settings.length > 0 &&
                    response.table_settings[0].type === 'row') {
                    // Already has row structure
                    setFormStructure(response.table_settings);

                    // Extract layout information
                    const layoutModes = {};
                    response.table_settings.forEach((row, index) => {
                        if (row.layout) {
                            layoutModes[index] = row.layout;
                        }
                    });
                    setRowLayoutMode(layoutModes);
                } else {
                    // Convert old format to new row-based structure
                    const initialRow = {
                        id: 'row-0',
                        type: 'row',
                        fields: response.table_settings || []
                    };
                    setFormStructure([initialRow]);
                }
            },
            error(error) {
                console.error('Error:', error);
            },
        });
    };

    const retrieveFormData = () => {
        wp.ajax.send('simpleform_get_tables', {
            data: {
                nonce: getNonce()
            },
            success(data) {
                setTotaltable(data.tables_count);
            },
            error({ message }) {
                // Handle error
            },
        });
    };

    useEffect(() => {
        if (mode === 'create') {
            retrieveFormData();
        }
    }, [mode]);

    const togglePreviewModal = () => setPreviewModalOpen(!isPreviewModalOpen);

    const handleClosePopup = () => {
        setCreateTableModal(false);
        setShowjson((showjson) => !showjson);
    };

    const handleHrefChange = (e) => {
        const hrefValue = e.target.value;
        const sanitizedHref = hrefValue.replace(/\bjavascript:/gi, '');
        setEditingField({ ...editingField, href: sanitizedHref });
    };

    const handleSrcChange = (e) => {
        const hrefValue = e.target.value;
        const sanitizedHref = hrefValue.replace(/\bjavascript:/gi, '');
        setEditingField({ ...editingField, src: sanitizedHref });
    };

    function handleCancelOutside(event: MouseEvent) {
        if (
            createTableModalRef.current &&
            !createTableModalRef.current.contains(event.target)
        ) {
            handleClosePopup();
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleCancelOutside);
        return () => {
            document.removeEventListener('mousedown', handleCancelOutside);
        };
    }, [handleCancelOutside]);

    // Add a new row to the form structure
    const addNewRow = () => {
        const newRowId = `row-${formStructure.length}`;
        setFormStructure([...formStructure, { id: newRowId, type: 'row', fields: [] }]);
        setActiveRow(formStructure.length);
    };

    // Toggle row layout between horizontal and vertical
    const toggleRowLayout = (rowIndex) => {
        setRowLayoutMode({
            ...rowLayoutMode,
            [rowIndex]: rowLayoutMode[rowIndex] === 'horizontal' ? 'vertical' : 'horizontal'
        });
    };

    // Get current row layout mode
    const getRowLayoutMode = (rowIndex) => {
        return rowLayoutMode[rowIndex] || 'vertical'; // Default to vertical
    };

    // Remove empty row
    const handleRemoveRow = (rowIndex) => {
        if (formStructure.length > 1) {
            setFormStructure(prevStructure => {
                return prevStructure.filter((_, index) => index !== rowIndex);
            });

            // Update active row if necessary
            if (activeRow >= rowIndex && activeRow > 0) {
                setActiveRow(activeRow - 1);
            }
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const sourceId = result.source.droppableId;
        const destId = result.destination.droppableId;
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        // Dragging from available fields to a specific row
        if (sourceId === 'available-fields' && destId.startsWith('row-')) {
            const rowIndex = parseInt(destId.split('-')[1]);
            const sourceField = availableFields[sourceIndex];
            const newField = {
                ...sourceField,
                id: `${sourceField.id}-${new Date().getTime()}`,
                name: `${sourceField.name}-${new Date().getTime()}`,
                uniqueId: `field-${new Date().getTime()}`,
            };

            setFormStructure(prevStructure => {
                const newStructure = [...prevStructure];
                if (!newStructure[rowIndex]) {
                    console.error('Row does not exist at index:', rowIndex);
                    return prevStructure;
                }
                if (!newStructure[rowIndex].fields) {
                    newStructure[rowIndex].fields = [];
                }
                newStructure[rowIndex].fields.splice(destinationIndex, 0, newField);
                return newStructure;
            });
        }
        // Moving within the same row
        else if (sourceId.startsWith('row-') && destId === sourceId) {
            const rowIndex = parseInt(sourceId.split('-')[1]);

            setFormStructure(prevStructure => {
                const newStructure = [...prevStructure];
                if (!newStructure[rowIndex]) {
                    console.error('Row does not exist at index:', rowIndex);
                    return prevStructure;
                }
                if (!newStructure[rowIndex].fields) {
                    newStructure[rowIndex].fields = [];
                    return newStructure;
                }
                if (newStructure[rowIndex].fields.length > 0) {
                    const [movedField] = newStructure[rowIndex].fields.splice(sourceIndex, 1);
                    newStructure[rowIndex].fields.splice(destinationIndex, 0, movedField);
                }
                return newStructure;
            });
        }
        // Moving between different rows
        else if (sourceId.startsWith('row-') && destId.startsWith('row-')) {
            const sourceRowIndex = parseInt(sourceId.split('-')[1]);
            const destRowIndex = parseInt(destId.split('-')[1]);

            setFormStructure(prevStructure => {
                const newStructure = [...prevStructure];
                if (!newStructure[sourceRowIndex] || !newStructure[destRowIndex]) {
                    console.error('Source or destination row does not exist');
                    return prevStructure;
                }
                if (!newStructure[sourceRowIndex].fields) {
                    newStructure[sourceRowIndex].fields = [];
                    return newStructure;
                }
                if (!newStructure[destRowIndex].fields) {
                    newStructure[destRowIndex].fields = [];
                }
                if (newStructure[sourceRowIndex].fields.length > 0) {
                    const [movedField] = newStructure[sourceRowIndex].fields.splice(sourceIndex, 1);
                    newStructure[destRowIndex].fields.splice(destinationIndex, 0, movedField);
                }
                return newStructure;
            });
        }
    };

    const handleRemoveField = (uniqueId) => {
        setFormStructure(prevStructure => {
            return prevStructure.map(row => ({
                ...row,
                fields: row.fields.filter(field => field.uniqueId !== uniqueId)
            }));
        });
        setEditingField(null);
    };

    const handleEditField = (uniqueId) => {
        let fieldToEdit = null;

        formStructure.forEach(row => {
            const found = row.fields.find(field => field.uniqueId === uniqueId);
            if (found) {
                fieldToEdit = found;
            }
        });

        if (fieldToEdit) {
            // Ensure conditional logic structure exists
            if (!fieldToEdit.conditionalLogic) {
                fieldToEdit.conditionalLogic = {
                    enabled: false,
                    conditions: [],
                    action: 'show'
                };
            }
            setEditingField({ ...fieldToEdit });
        }
    };

    const handleUpdateField = () => {
        setFormStructure(prevStructure => {
            return prevStructure.map(row => ({
                ...row,
                fields: row.fields.map(field => {
                    if (field.uniqueId === editingField.uniqueId) {
                        return { ...editingField };
                    }
                    return field;
                })
            }));
        });
        setEditingField(null);
        setIsEditingField(false);
    };

    // Realtime update
    useEffect(() => {
        if (editingField) {
            setFormStructure(prevStructure => {
                return prevStructure.map(row => ({
                    ...row,
                    fields: row.fields.map(field => {
                        if (field.uniqueId === editingField.uniqueId) {
                            return { ...editingField };
                        }
                        return field;
                    })
                }));
            });
        }
    }, [editingField]);

    const handleShowJsonForm = () => {
        const structuredFormData = formStructure.map(row => {
            return {
                id: row.id,
                type: 'row',
                layout: getRowLayoutMode(parseInt(row.id.split('-')[1])),
                fields: row.fields
            };
        });

        setFormData(structuredFormData);
        setShowjson((showjson) => !showjson);
        setCreateTableModal(true);
    };

    const handleCopyShortcode = async (id) => {
        const shortcode = `[simple_form id="${id}"]`;

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shortcode);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = shortcode;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            setCopySuccess(true);
            setModalConfig({
                isOpen: true,
                type: 'toast',
                title: 'Copied',
                message: 'Shortcode has been copied!',
                position: 'top-right'
            });
            setTimeout(closeModal, 3000);
            setTimeout(() => {
                setCopySuccess(false);
            }, 1000);

        } catch (err) {
            setCopySuccess(false);
            setModalConfig({
                isOpen: true,
                type: 'toast',
                title: 'Failed',
                message: 'Shortcode copy failed!',
                position: 'top-right'
            });
            setTimeout(closeModal, 3000);
        }
    };

    const handleSaveForm = () => {
        if (mode === 'create' && totaltable > 4 && !allConditionsMet) {
            setModalConfig({
                isOpen: true,
                type: 'premium',
                title: '🔥 Premium Feature',
                message: 'Want to build more forms? Go premium and enjoy unlimited form creation plus exclusive features!',
                onConfirm: () => {
                    window.open('https://functiondeck.com/', '_blank');
                    closeModal();
                },
                confirmText: 'Upgrade Now',
                declineText: 'Maybe Later'
            });
            return;
        }

        const structuredFormData = formStructure.map(row => {
            return {
                id: row.id,
                type: 'row',
                layout: getRowLayoutMode(parseInt(row.id.split('-')[1])),
                fields: row.fields
            };
        });

        setFormData(structuredFormData);
        setShowjson((showjson) => !showjson);

        const modalTitle = mode === 'create' ? 'Save Form' : 'Update Form';
        const modalMessage = mode === 'create' ? 'Are you done!' : 'Are you sure you want to save the form?';
        const confirmText = mode === 'create' ? 'Yes, Save!' : 'Yes, Save!';

        setModalConfig({
            isOpen: true,
            type: 'confirmation',
            title: modalTitle,
            message: modalMessage,
            onConfirm: () => {
                // Use React state values directly instead of DOM elements
                const formNameValue = formName || 'Untitled';
                const btnNameValue = btnName || 'Submit';

                if (mode === 'create') {
                    wp.ajax.send('simpleform_create_form', {
                        data: {
                            nonce: getNonce(),
                            name: formNameValue,
                            btn_name: btnNameValue,
                            formdata: structuredFormData,
                            thankyou_page_enabled: thankyouPageEnabled,
                            thankyou_page_id: thankyouPageId,
                            thankyou_page_type: thankyouPageType,
                            thankyou_custom_url: thankyouCustomUrl,
                            thankyou_open_new_tab: thankyouOpenNewTab,
                            email_notification_enabled: emailNotificationEnabled,
                            notification_emails: JSON.stringify(notificationEmails),
                            email_subject: emailSubject,
                            email_message: emailMessage,
                        },
                        success({ id }) {
                            setModalConfig({
                                isOpen: true,
                                type: 'toast',
                                title: 'success!',
                                message: 'Your Form has been saved',
                                position: 'top-right'
                            });
                            retrieveFormData();
                            navigate(`/`);
                        },
                        error({ message }) {
                            // Handle error
                        },
                    });
                } else {
                    // Use React state value directly instead of DOM element
                    const formid = formJson.id || id;

                    wp.ajax.send('simpleform_save_table', {
                        data: {
                            nonce: getNonce(),
                            id: formid,
                            name: formNameValue,
                            btn_name: btnNameValue,
                            formdata: structuredFormData,
                            thankyou_page_enabled: thankyouPageEnabled,
                            thankyou_page_id: thankyouPageId,
                            thankyou_page_type: thankyouPageType,
                            thankyou_custom_url: thankyouCustomUrl,
                            thankyou_open_new_tab: thankyouOpenNewTab,
                            email_notification_enabled: emailNotificationEnabled,
                            notification_emails: JSON.stringify(notificationEmails),
                            email_subject: emailSubject,
                            email_message: emailMessage,
                        },
                        success({ id }) {
                            setModalConfig({
                                isOpen: true,
                                type: 'toast',
                                title: 'success!',
                                message: 'Your Form has been saved',
                                position: 'top-right'
                            });
                            setTimeout(closeModal, 3000);
                        },
                        error({ message }) {
                            // Handle error
                        },
                    });
                }

                closeModal();
            },
            confirmText: confirmText,
            declineText: 'Cancel'
        });
    };

    const handleAddOption = () => {
        if (editingField && (editingField.type === 'select' || editingField.type === 'radio' || editingField.type === 'checkbox')) {
            const updatedOptions = [
                ...editingField.options,
                { label: 'New Option', value: 'New Option' },
            ];
            setEditingField({ ...editingField, options: updatedOptions });
        }
    };

    const handleRemoveOption = (optionIndex) => {
        if (
            editingField &&
            (editingField.type === 'select' || editingField.type === 'radio' || editingField.type === 'checkbox') &&
            optionIndex !== null
        ) {
            const updatedOptions = [...editingField.options];
            updatedOptions.splice(optionIndex, 1);
            setEditingField({ ...editingField, options: updatedOptions });
            setEditingOptionIndex(null);
        }
    };

    const handleEditOption = (index) => {
        setEditingOptionIndex(index);
    };
    return (
        <div className='simple-form-builder'>
            {/* Modern Clean Header */}
            <div className="compact-form-header">
                <div className="header-main">
                    <DynamicFormIcon className="header-icon" />

                    <div className="title-section">
                        <h2>Form Builder</h2>
                        <span className="subtitle">{mode === 'create' ? 'Create New' : 'Edit Form'}</span>
                    </div>

                    <div className="form-name-input">
                        <input
                            type="text"
                            value={formName}
                            placeholder="Form name..."
                            className="form-name-field"
                            id="formName"
                            onChange={(e) => setFormName(e.target.value)}
                        />
                        {mode === 'edit' && (
                            <input type="hidden" name="formid" id="formid" value={formJson.id || ''} />
                        )}
                    </div>

                    {mode === 'edit' && (
                        <div className="shortcode-section">
                            <code className="shortcode-display">
                                [simple_form id={formJson.id}]
                            </code>
                            <button
                                className="copy-btn"
                                onClick={() => handleCopyShortcode(formJson.id)}
                                title="Copy Shortcode"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>

                <div className="header-actions">
                    <button
                        className="action-btn settings-btn"
                        onClick={() => setShowFormSettingsModal(true)}
                        title="Settings"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                        </svg>
                        <span className="btn-text">Settings</span>
                    </button>

                    <button
                        className="action-btn preview-btn"
                        onClick={togglePreviewModal}
                        title="Preview"
                    >
                        <VisibilityIcon />
                        <span className="btn-text">Preview</span>
                    </button>

                    {!editingField && (
                        <button
                            className="action-btn json-btn"
                            onClick={handleShowJsonForm}
                            title="Toggle JSON"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14,2 14,8 20,8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10,9 9,9 8,9"></polyline>
                            </svg>
                            <span className="btn-text">{showjson ? 'Hide' : 'JSON'}</span>
                        </button>
                    )}

                    <button
                        className="action-btn save-btn"
                        onClick={handleSaveForm}
                        title="Save Form"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                            <polyline points="17,21 17,13 7,13 7,21"></polyline>
                            <polyline points="7,3 7,8 15,8"></polyline>
                        </svg>
                        <span className="btn-text">{mode === 'create' ? 'Save' : 'Update'}</span>
                    </button>
                </div>
            </div>

            {/* Form Settings Modal */}
            {showFormSettingsModal && (
                <Modal>
                    <div className="form-settings-modal">
                        <div className="modal-header">
                            <h3>Form Settings</h3>
                            <button
                                className="close-btn"
                                onClick={() => setShowFormSettingsModal(false)}
                            >
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="modal-content">
                            <div className="settings-section">
                                <h4>Basic Settings</h4>

                                <div className="form-group">
                                    <label htmlFor="modal_btn_name">Submit Button Text</label>
                                    <input
                                        type="text"
                                        value={btnName}
                                        placeholder='Submit button text'
                                        id="btn_name"
                                        onChange={(e) => setBtnName(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="settings-section">
                                <h4>Thank You Page</h4>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={thankyouPageEnabled}
                                            onChange={(e) => setThankyouPageEnabled(e.target.checked)}
                                            disabled={!allConditionsMet}
                                        />
                                        <span className="checkmark"></span>
                                        Redirect to Thank You Page after submission
                                    </label>
                                    {!allConditionsMet && (
                                        <small className="help-text locked-feature">
                                            🔒 This feature is locked. Upgrade to Pro to unlock thank you page redirection.
                                        </small>
                                    )}
                                </div>

                                {thankyouPageEnabled && (
                                    <>
                                        <div className="form-group">
                                            <label>Redirect Type</label>
                                            <div className="radio-group">
                                                <label className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="thankyouPageType"
                                                        value="page"
                                                        checked={thankyouPageType === 'page'}
                                                        onChange={(e) => setThankyouPageType(e.target.value)}
                                                        disabled={!allConditionsMet}
                                                    />
                                                    <span className="radio-checkmark"></span>
                                                    WordPress Page
                                                </label>
                                                <label className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="thankyouPageType"
                                                        value="url"
                                                        checked={thankyouPageType === 'url'}
                                                        onChange={(e) => setThankyouPageType(e.target.value)}
                                                        disabled={!allConditionsMet}
                                                    />
                                                    <span className="radio-checkmark"></span>
                                                    Custom URL
                                                </label>
                                            </div>
                                        </div>

                                        {thankyouPageType === 'page' && (
                                            <div className="form-group">
                                                <label htmlFor="thankyouPageId">Select Thank You Page</label>
                                                <select
                                                    id="thankyouPageId"
                                                    value={thankyouPageId}
                                                    onChange={(e) => setThankyouPageId(parseInt(e.target.value))}
                                                    disabled={!allConditionsMet}
                                                >
                                                    <option value={0}>Select a page...</option>
                                                    {wordpressPages.map((page) => (
                                                        <option key={page.id} value={page.id}>
                                                            {page.title}
                                                        </option>
                                                    ))}
                                                </select>
                                                <small className="help-text">
                                                    Users will be redirected to this WordPress page after successful form submission
                                                </small>
                                            </div>
                                        )}

                                        {thankyouPageType === 'url' && (
                                            <div className="form-group">
                                                <label htmlFor="thankyouCustomUrl">Custom URL</label>
                                                <input
                                                    type="url"
                                                    id="thankyouCustomUrl"
                                                    value={thankyouCustomUrl}
                                                    onChange={(e) => setThankyouCustomUrl(e.target.value)}
                                                    placeholder="https://example.com/thank-you"
                                                    disabled={!allConditionsMet}
                                                />
                                                <small className="help-text">
                                                    Enter the full URL where users should be redirected after form submission
                                                </small>
                                            </div>
                                        )}

                                        <div className="form-group">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={thankyouOpenNewTab}
                                                    onChange={(e) => setThankyouOpenNewTab(e.target.checked)}
                                                    disabled={!allConditionsMet}
                                                />
                                                <span className="checkmark"></span>
                                                Open in new tab
                                            </label>
                                            <small className="help-text">
                                                Check this option to open the thank you page in a new browser tab
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="settings-section">
                                <h4>Email Notifications</h4>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={emailNotificationEnabled}
                                            onChange={(e) => setEmailNotificationEnabled(e.target.checked)}
                                            disabled={!allConditionsMet}
                                        />
                                        <span className="checkmark"></span>
                                        Enable email notifications for form submissions
                                    </label>
                                    {!allConditionsMet && (
                                        <small className="help-text locked-feature">
                                            🔒 This feature is locked. Upgrade to Pro to unlock email notifications.
                                        </small>
                                    )}
                                </div>

                                {emailNotificationEnabled && (
                                    <>
                                        <div className="form-group">
                                            <label>Notification Recipients</label>
                                            {notificationEmails.map((email, index) => (
                                                <div key={index} className="email-input-group">
                                                    <input
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => {
                                                            const newEmails = [...notificationEmails];
                                                            newEmails[index] = e.target.value;
                                                            setNotificationEmails(newEmails);
                                                        }}
                                                        placeholder="Enter email address"
                                                        disabled={!allConditionsMet}
                                                    />
                                                    {notificationEmails.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="remove-email-btn"
                                                            onClick={() => {
                                                                const newEmails = notificationEmails.filter((_, i) => i !== index);
                                                                setNotificationEmails(newEmails);
                                                            }}
                                                            disabled={!allConditionsMet}
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                className="add-email-btn"
                                                onClick={() => setNotificationEmails([...notificationEmails, ''])}
                                                disabled={!allConditionsMet}
                                            >
                                                + Add Another Email
                                            </button>
                                            <small className="help-text">
                                                Add email addresses that should receive notifications when forms are submitted
                                            </small>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="emailSubject">Email Subject</label>
                                            <input
                                                type="text"
                                                id="emailSubject"
                                                value={emailSubject}
                                                onChange={(e) => setEmailSubject(e.target.value)}
                                                placeholder="New Form Submission"
                                                disabled={!allConditionsMet}
                                            />
                                            <small className="help-text">
                                                Subject line for notification emails. You can use {'{FormName}'} placeholder.
                                            </small>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="emailMessage">Email Message</label>
                                            <textarea
                                                id="emailMessage"
                                                value={emailMessage}
                                                onChange={(e) => setEmailMessage(e.target.value)}
                                                rows={4}
                                                placeholder="You have received a new form submission."
                                                disabled={!allConditionsMet}
                                            />
                                            <small className="help-text">
                                                Custom message for notification emails. Available placeholders: {'{Leads}'}, {'{MetaData}'}, {'{SiteURL}'}, {'{FormName}'}
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>

                            {mode === 'edit' && (
                                <div className="settings-section">
                                    <h4>Shortcode & Usage</h4>

                                    <div className="form-group">
                                        <label>Form Shortcode</label>
                                        <div className="shortcode-container">
                                            <code className="shortcode-display">
                                                [simple_form id={formJson.id}]
                                            </code>
                                            <button
                                                className="copy-shortcode-btn"
                                                onClick={() => handleCopyShortcode(formJson.id)}
                                                title="Copy Shortcode"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                </svg>
                                            </button>
                                        </div>
                                        <small className="help-text">
                                            Copy this shortcode and paste it into any post, page, or widget where you want the form to appear.
                                        </small>
                                    </div>

                                    <div className="form-group">
                                        <label>Advanced Usage</label>
                                        <div className="advanced-examples">
                                            <div className="example-item">
                                                <code>[simple_form id={formJson.id} position="bottom-right" theme="theme-2"]</code>
                                                <small>Floating form with custom theme</small>
                                            </div>
                                            <div className="example-item">
                                                <code>[simple_form id={formJson.id} position="inline"]</code>
                                                <small>Inline form display</small>
                                            </div>
                                        </div>
                                        <small className="help-text">
                                            Available positions: <strong>bottom-right</strong>, <strong>bottom-left</strong>, <strong>inline</strong>
                                        </small>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn secondary"
                                onClick={() => setShowFormSettingsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Drag & Drop Available Fields */}
            <div className="form-builder-container">
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="form-builder">
                        <div className="form-fields">
                            <h3>Available Fields</h3>
                            <Droppable droppableId="available-fields" direction="vertical">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="draggable-field-container"
                                    >
                                        {availableFields.map((field, index) => (
                                            <Draggable key={field.id} draggableId={field.id} index={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={`draggable-field ${(field.pro === 'pro-inactive' && field.profeature === 'true') ? 'locked' : ''}`}
                                                    >
                                                        <div className="field-icons">
                                                            {field.type === 'text' && <button className="field-icon">{sfText}</button>}
                                                            {field.type === 'number' && <button className="field-icon">{sfNumber}</button>}
                                                            {field.type === 'email' && <button className="field-icon">{sfEmail}</button>}
                                                            {field.type === 'image' && <button className="field-icon">{sfImage}</button>}
                                                            {field.type === 'textarea' && <button className="field-icon">{sfTextarea}</button>}
                                                            {field.type === 'radio' && <button className="field-icon">{sfRadio}</button>}
                                                            {field.type === 'checkbox' && <button className="field-icon">{sfCheckbox}</button>}
                                                            {field.type === 'select' && <button className="field-icon">{sfSelect}</button>}
                                                            {field.type === 'customcaptcha' && <button className="field-icon">{sfCustomCaptcha}</button>}
                                                            {field.type === 'page_break' && <button className="field-icon">{sfPagebreak}</button>}
                                                            {field.type === 'file' && <button className="field-icon">{sfFile}</button>}
                                                            {field.type === 'cloudflare' && <button className="field-icon">{sfCloudflare}</button>}
                                                            {field.type === 'esignature' && <button className="field-icon">{sfSign}</button>}
                                                            {field.type === 'range' && <button className="field-icon">{sfRange}</button>}
                                                            {field.type === 'week' && <button className="field-icon">{sfWeek}</button>}
                                                            {field.type === 'url' && <button className="field-icon">{sfUrl}</button>}
                                                            {field.type === 'time' && <button className="field-icon">{sfClock}</button>}
                                                            {field.type === 'tel' && <button className="field-icon">{sfTelephone}</button>}
                                                            {field.type === 'submit' && <button className="field-icon">{sfSubmit}</button>}
                                                            {field.type === 'button' && <button className="field-icon">{sfBtn}</button>}
                                                            {field.type === 'password' && <button className="field-icon">{sfpass}</button>}
                                                            {field.type === 'date' && <button className="field-icon">{sfDate}</button>}
                                                            {field.type === 'datetime-local' && <button className="field-icon">{sfDatetime}</button>}
                                                            {field.type === 'color' && <button className="field-icon">{sfColor}</button>}
                                                            {field.type === 'hidden' && <button className="field-icon">{sfHide}</button>}
                                                            {field.type === 'heading' && <button className="field-icon">{sfHeading}</button>}
                                                            {field.type === 'label' && <button className="field-icon">{sfLabel}</button>}
                                                            {field.type === 'link' && <button className="field-icon">{sfLink}</button>}
                                                            {field.type === 'video' && <button className="field-icon">{sfVideo}</button>}
                                                            {field.type === 'audio' && <button className="field-icon">{sfAudio}</button>}
                                                            {field.type === 'iframe' && <button className="field-icon">{sfiFrame}</button>}
                                                        </div>

                                                        {field.label}

                                                        {(field.pro === 'pro-inactive' && field.profeature === 'true') && <div className="PRO_TAG">PRO</div>}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>

                        <div className="form-canvas">
                            <h3>Form Canvas <button className="add-row-btn" onClick={addNewRow}>Add Row <AddIcon /></button></h3>

                            <div className="form-canvas-rows">
                                {formStructure.map((row, rowIndex) => {
                                    const rowId = row.id;
                                    const isHorizontal = getRowLayoutMode(rowIndex) === 'horizontal';

                                    return (
                                        <div key={rowId} className={`form-row ${rowIndex === activeRow ? 'active-row' : ''}`}>
                                            <div className="row-header">
                                                <span>Row {rowIndex + 1}</span>
                                                <div className="row-controls">
                                                    <button
                                                        className={`layout-toggle ${isHorizontal ? 'horizontal' : 'vertical'}`}
                                                        onClick={() => toggleRowLayout(rowIndex)}
                                                        title={isHorizontal ? "Switch to vertical layout" : "Switch to horizontal layout"}
                                                    >
                                                        {isHorizontal ? <ViewListIcon /> : <GridViewIcon />}
                                                    </button>
                                                    {formStructure.length > 1 && (
                                                        <button
                                                            className="remove-row-btn"
                                                            onClick={() => handleRemoveRow(rowIndex)}
                                                            title="Remove row"
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <Droppable
                                                droppableId={rowId}
                                                direction={isHorizontal ? "horizontal" : "vertical"}
                                            >
                                                {(provided) => (
                                                    <div
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                        className={`form-canvas-container ${isHorizontal ? 'horizontal-layout' : 'vertical-layout'}`}
                                                        onClick={() => setActiveRow(rowIndex)}
                                                    >
                                                        {(row && row.fields && Array.isArray(row.fields) ? row.fields : []).map((field, index) => (
                                                            <Draggable key={field.uniqueId} draggableId={field.uniqueId} index={index}>
                                                                {(provided) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={`draggable-field ${isHorizontal ? 'field-horizontal' : ''} ${isEditingField && field && editingField && field.uniqueId === editingField.uniqueId
                                                                            ? 'remove-background'
                                                                            : ''
                                                                            }`}
                                                                    >
                                                                        <RenderField field={field} />
                                                                        <button
                                                                            className={`form-edit ${isEditingField && field && editingField && field.uniqueId === editingField.uniqueId
                                                                                ? 'remove-background'
                                                                                : ''
                                                                                }`}
                                                                            onClick={() => {
                                                                                handleEditField(field.uniqueId);
                                                                                setIsEditingField(true);
                                                                            }}
                                                                        >
                                                                            {EditIcon}
                                                                        </button>
                                                                        <button className='form-remove' onClick={() => handleRemoveField(field.uniqueId)}>
                                                                            {DeleteIcon}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </DragDropContext>
                {editingField && (
                    <div className="edit-field-form">
                        <div className='form-btn-group'>
                            <button className='jsonbtn' onClick={() => { handleUpdateField(); setIsEditingField(false); }} >Update</button>
                            <button className='jsonbtn' onClick={handleShowJsonForm}>{showjson ? 'Json viewed' : 'Show Json'}</button>
                        </div>
                        <h3>Edit Field</h3>
                        <div className="edit-fields-section">
                            <div className='simple-form-id-panel'>
                                <label>ID:</label>
                                <input
                                    type="text"
                                    value={editingField.id}
                                    onChange={(e) => setEditingField({ ...editingField, id: e.target.value })}
                                />
                            </div>

                            <div className='simple-form-id-panel'>
                                <label>Name:</label>
                                <input
                                    type="text"
                                    value={editingField.name}
                                    onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
                                />
                            </div>

                            <div className='simple-form-id-panel'>
                                <label>Label:</label>
                                <input
                                    type="text"
                                    value={editingField.label}
                                    onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                                />
                            </div>

                            <div className='simple-form-id-panel type-dropdown'>
                                <label>Type:</label>
                                {!(editingField.type === 'file' || editingField.type === 'select' || editingField.type === 'checkbox' || editingField.type === 'radio') ? (
                                    <select
                                        className='select-type-class'
                                        value={editingField.type}
                                        onChange={(e) => setEditingField({ ...editingField, type: e.target.value })}
                                    >
                                        <option value="button">Button</option>
                                        <option value="color">Color</option>
                                        <option value="date">Date</option>
                                        <option value="datetime-local">Datetime-local</option>
                                        <option value="email">Email</option>
                                        <option value="hidden">Hidden</option>
                                        <option value="image">Image</option>
                                        <option value="month">Month</option>
                                        <option value="number">Number</option>
                                        <option value="password">Password</option>
                                        <option value="range">Range</option>
                                        <option value="reset">Reset</option>
                                        <option value="search">Search</option>
                                        <option value="submit">Submit</option>
                                        <option value="tel">Tel</option>
                                        <option value="text">Text</option>
                                        <option value="text">Text Input</option>
                                        <option value="textarea">Text Area</option>
                                        <option value="time">Time</option>
                                        <option value="url">URL</option>
                                        <option value="week">Week</option>
                                        <option value="cloudflare">Cloudflare</option>
                                        <option value="esignature">E-signature</option>
                                        <option value="customcaptcha">Custom captcha</option>
                                        <option value="page_break">Page break</option>
                                        <option value="heading">Heading</option>
                                        <option value="label">Label</option>
                                        <option value="link">Link</option>
                                        <option value="video">Video</option>
                                        <option value="audio">Audio</option>
                                        <option value="iframe">iframe</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={editingField.type}
                                        onChange={(e) => setEditingField({ ...editingField, type: e.target.value })}
                                    />
                                )}
                            </div>

                            <div className='simple-form-id-panel'>
                                <label>Subtype:</label>
                                <input
                                    type="text"
                                    value={editingField.subtype}
                                    onChange={(e) => setEditingField({ ...editingField, subtype: e.target.value })}
                                />
                            </div>

                            <div className='simple-form-id-panel'>
                                <label>Placeholder:</label>
                                <input
                                    type="text"
                                    value={editingField.placeholder}
                                    onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                                />
                            </div>

                            {editingField.type === 'image' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Source:</label>
                                        <input
                                            type="text"
                                            value={editingField.src}
                                            onChange={handleSrcChange}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Width:</label>
                                        <input
                                            type="text"
                                            value={editingField.width}
                                            onChange={(e) => setEditingField({ ...editingField, width: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Height:</label>
                                        <input
                                            type="text"
                                            value={editingField.height}
                                            onChange={(e) => setEditingField({ ...editingField, height: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Alt:</label>
                                        <input
                                            type="text"
                                            value={editingField.alt}
                                            onChange={(e) => setEditingField({ ...editingField, alt: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}

                            {editingField.type === 'number' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Min:</label>
                                        <input
                                            type="text"
                                            value={editingField.min}
                                            onChange={(e) => setEditingField({ ...editingField, min: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Max:</label>
                                        <input
                                            type="text"
                                            value={editingField.max}
                                            onChange={(e) => setEditingField({ ...editingField, max: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}

                            {editingField.type === 'heading' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Content:</label>
                                        <input
                                            type="text"
                                            value={editingField.content}
                                            onChange={(e) => setEditingField({ ...editingField, content: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel type-dropdown'>
                                        <label>Heading Type:</label>
                                        <select
                                            className='select-type-class'
                                            value={editingField.headingtype}
                                            onChange={(e) => setEditingField({ ...editingField, headingtype: e.target.value })}
                                        >
                                            <option value="h1">H1</option>
                                            <option value="h2">H2</option>
                                            <option value="h3">H3</option>
                                            <option value="h4">H4</option>
                                            <option value="h5">H5</option>
                                            <option value="h6">H6</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {editingField.type === 'label' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Content:</label>
                                        <input
                                            type="text"
                                            value={editingField.content}
                                            onChange={(e) => setEditingField({ ...editingField, content: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>For value:</label>
                                        <input
                                            type="text"
                                            value={editingField.for}
                                            onChange={(e) => setEditingField({ ...editingField, for: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}

                            {editingField.type === 'link' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Content:</label>
                                        <input
                                            type="text"
                                            value={editingField.content}
                                            onChange={(e) => setEditingField({ ...editingField, content: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>href:</label>
                                        <input
                                            type="text"
                                            value={editingField.href}
                                            onChange={handleHrefChange}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel type-dropdown'>
                                        <label>Redirection Type:</label>
                                        <select
                                            className='select-type-class'
                                            value={editingField.target}
                                            onChange={(e) => setEditingField({ ...editingField, target: e.target.value })}
                                        >
                                            <option value="_blank">New Tab</option>
                                            <option value="_self">Same Tab</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {editingField.type === 'video' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Content:</label>
                                        <input
                                            type="text"
                                            value={editingField.content}
                                            onChange={(e) => setEditingField({ ...editingField, content: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Width:</label>
                                        <input
                                            type="text"
                                            value={editingField.width}
                                            onChange={(e) => setEditingField({ ...editingField, width: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Height:</label>
                                        <input
                                            type="text"
                                            value={editingField.height}
                                            onChange={(e) => setEditingField({ ...editingField, height: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Source (src):</label>
                                        <input
                                            type="text"
                                            value={editingField.src}
                                            onChange={handleSrcChange}
                                        />
                                    </div>
                                </>
                            )}

                            {editingField.type === 'audio' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Content:</label>
                                        <input
                                            type="text"
                                            value={editingField.content}
                                            onChange={(e) => setEditingField({ ...editingField, content: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Width:</label>
                                        <input
                                            type="text"
                                            value={editingField.width}
                                            onChange={(e) => setEditingField({ ...editingField, width: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Height:</label>
                                        <input
                                            type="text"
                                            value={editingField.height}
                                            onChange={(e) => setEditingField({ ...editingField, height: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Source (src):</label>
                                        <input
                                            type="text"
                                            value={editingField.src}
                                            onChange={handleSrcChange}
                                        />
                                    </div>
                                </>
                            )}

                            {editingField.type === 'iframe' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Content:</label>
                                        <input
                                            type="text"
                                            value={editingField.content}
                                            onChange={(e) => setEditingField({ ...editingField, content: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Width:</label>
                                        <input
                                            type="text"
                                            value={editingField.width}
                                            onChange={(e) => setEditingField({ ...editingField, width: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Height:</label>
                                        <input
                                            type="text"
                                            value={editingField.height}
                                            onChange={(e) => setEditingField({ ...editingField, height: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Source (src):</label>
                                        <input
                                            type="text"
                                            value={editingField.src}
                                            onChange={handleSrcChange}
                                        />
                                    </div>
                                </>
                            )}

                            {editingField.type === 'cloudflare' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Site Key:</label>
                                        <input
                                            type="text"
                                            value={editingField.sitekey}
                                            onChange={(e) => setEditingField({ ...editingField, sitekey: e.target.value })}
                                        />
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Widgets Theme:</label>
                                        <select
                                            className='select-type-class'
                                            value={editingField.theme}
                                            onChange={(e) => setEditingField({ ...editingField, theme: e.target.value })}
                                        >
                                            <option value="light">Light</option>
                                            <option value="dark">Dark</option>
                                            <option value="auto">Auto</option>
                                        </select>
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>
                                            Enable Button access:
                                            <input
                                                type="checkbox"
                                                checked={editingField.btnaccess === 'true'}
                                                onChange={(e) => setEditingField({ ...editingField, btnaccess: e.target.checked ? 'true' : 'false' })}
                                            />
                                        </label>
                                    </div>
                                    <div className='simple-form-id-panel'>
                                        <label>Error message:</label>
                                        <input
                                            type="text"
                                            value={editingField.error}
                                            onChange={(e) => setEditingField({ ...editingField, error: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}

                            {editingField.type === 'checkbox' && (
                                <div className='simple-form-id-panel'>
                                    <label>Toggle:
                                        <input
                                            type="checkbox"
                                            checked={editingField.toggle === 'true'}
                                            onChange={(e) => {
                                                setEditingField({ ...editingField, toggle: e.target.checked ? 'true' : 'false' });
                                            }}
                                        />
                                    </label>
                                </div>
                            )}

                            {editingField.type === 'page_break' && (
                                <>
                                    <div className='simple-form-id-panel'>
                                        <label>Show Progress Indicator:
                                            <input
                                                type="checkbox"
                                                checked={editingField.showProgressIndicator === true || editingField.showProgressIndicator === 'true'}
                                                onChange={(e) => {
                                                    setEditingField({ ...editingField, showProgressIndicator: e.target.checked });
                                                }}
                                            />
                                        </label>
                                    </div>
                                    {(editingField.showProgressIndicator === true || editingField.showProgressIndicator === 'true') && (
                                        <>
                                            <div className='simple-form-id-panel'>
                                                <label>Progress Label:</label>
                                                <input
                                                    type="text"
                                                    value={editingField.progressLabel || 'Step'}
                                                    onChange={(e) => setEditingField({ ...editingField, progressLabel: e.target.value })}
                                                />
                                            </div>
                                            <div className='simple-form-id-panel type-dropdown'>
                                                <label>Progress Style:</label>
                                                <select
                                                    className='select-type-class'
                                                    value={editingField.progressStyle || 'dots'}
                                                    onChange={(e) => setEditingField({ ...editingField, progressStyle: e.target.value })}
                                                >
                                                    <option value="dots">Dots</option>
                                                    <option value="numbers">Numbers</option>
                                                    <option value="bar">Progress Bar</option>
                                                </select>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Conditional Logic Section */}
                            {editingField.type !== 'page_break' && editingField.type !== 'submit' && (
                                <div className='conditional-logic-section'>
                                    <h4>Conditional Logic</h4>
                                    <div className='simple-form-id-panel'>
                                        <label>Enable Conditional Logic:
                                            <input
                                                type="checkbox"
                                                checked={editingField.conditionalLogic?.enabled === true || editingField.conditionalLogic?.enabled === 'true'}
                                                onChange={(e) => {
                                                    const conditionalLogic = editingField.conditionalLogic || { enabled: false, conditions: [], action: 'show' };
                                                    setEditingField({
                                                        ...editingField,
                                                        conditionalLogic: {
                                                            ...conditionalLogic,
                                                            enabled: e.target.checked
                                                        }
                                                    });
                                                }}
                                            />
                                        </label>
                                    </div>

                                    {editingField.conditionalLogic?.enabled && (
                                        <>
                                            <div className='simple-form-id-panel type-dropdown'>
                                                <label>Action:</label>
                                                <select
                                                    className='select-type-class'
                                                    value={editingField.conditionalLogic?.action || 'show'}
                                                    onChange={(e) => {
                                                        const conditionalLogic = editingField.conditionalLogic || { enabled: true, conditions: [], action: 'show' };
                                                        setEditingField({
                                                            ...editingField,
                                                            conditionalLogic: {
                                                                ...conditionalLogic,
                                                                action: e.target.value
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <option value="show">Show this field</option>
                                                    <option value="hide">Hide this field</option>
                                                </select>
                                            </div>

                                            <div className='conditions-list'>
                                                <h5>Conditions:</h5>
                                                {(editingField.conditionalLogic?.conditions || []).map((condition, index) => (
                                                    <div key={index} className='condition-item simple-form-id-panel'>
                                                        <select
                                                            className='select-type-class'
                                                            value={condition.field || ''}
                                                            onChange={(e) => {
                                                                const conditions = [...(editingField.conditionalLogic?.conditions || [])];
                                                                conditions[index] = { ...condition, field: e.target.value };
                                                                const conditionalLogic = editingField.conditionalLogic || { enabled: true, conditions: [], action: 'show' };
                                                                setEditingField({
                                                                    ...editingField,
                                                                    conditionalLogic: {
                                                                        ...conditionalLogic,
                                                                        conditions
                                                                    }
                                                                });
                                                            }}
                                                        >
                                                            <option value="">Select Field</option>
                                                            {formFields.filter(f => f.uniqueId !== editingField.uniqueId &&
                                                                ['text', 'email', 'number', 'select', 'radio', 'checkbox', 'textarea', 'url', 'tel', 'date', 'time'].includes(f.type)
                                                            ).map(field => (
                                                                <option key={field.uniqueId} value={field.uniqueId}>
                                                                    {field.label || field.name}
                                                                </option>
                                                            ))}
                                                        </select>

                                                        <select
                                                            className='select-type-class'
                                                            value={condition.operator || 'equals'}
                                                            onChange={(e) => {
                                                                const conditions = [...(editingField.conditionalLogic?.conditions || [])];
                                                                conditions[index] = { ...condition, operator: e.target.value };
                                                                const conditionalLogic = editingField.conditionalLogic || { enabled: true, conditions: [], action: 'show' };
                                                                setEditingField({
                                                                    ...editingField,
                                                                    conditionalLogic: {
                                                                        ...conditionalLogic,
                                                                        conditions
                                                                    }
                                                                });
                                                            }}
                                                        >
                                                            <option value="equals">Equals</option>
                                                            <option value="not_equals">Not Equals</option>
                                                            <option value="contains">Contains</option>
                                                            <option value="not_contains">Not Contains</option>
                                                            <option value="is_empty">Is Empty</option>
                                                            <option value="is_not_empty">Is Not Empty</option>
                                                        </select>

                                                        {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                                                            <input
                                                                type="text"
                                                                placeholder="Value"
                                                                value={condition.value || ''}
                                                                onChange={(e) => {
                                                                    const conditions = [...(editingField.conditionalLogic?.conditions || [])];
                                                                    conditions[index] = { ...condition, value: e.target.value };
                                                                    const conditionalLogic = editingField.conditionalLogic || { enabled: true, conditions: [], action: 'show' };
                                                                    setEditingField({
                                                                        ...editingField,
                                                                        conditionalLogic: {
                                                                            ...conditionalLogic,
                                                                            conditions
                                                                        }
                                                                    });
                                                                }}
                                                            />
                                                        )}

                                                        <button
                                                            className='option-remover-multiple'
                                                            onClick={() => {
                                                                const conditions = [...(editingField.conditionalLogic?.conditions || [])];
                                                                conditions.splice(index, 1);
                                                                const conditionalLogic = editingField.conditionalLogic || { enabled: true, conditions: [], action: 'show' };
                                                                setEditingField({
                                                                    ...editingField,
                                                                    conditionalLogic: {
                                                                        ...conditionalLogic,
                                                                        conditions
                                                                    }
                                                                });
                                                            }}
                                                        >
                                                            <CloseIcon className='add-options' />
                                                        </button>
                                                    </div>
                                                ))}

                                                <button
                                                    className='jsonbtn'
                                                    onClick={() => {
                                                        const conditions = [...(editingField.conditionalLogic?.conditions || [])];
                                                        conditions.push({ field: '', operator: 'equals', value: '' });
                                                        const conditionalLogic = editingField.conditionalLogic || { enabled: true, conditions: [], action: 'show' };
                                                        setEditingField({
                                                            ...editingField,
                                                            conditionalLogic: {
                                                                ...conditionalLogic,
                                                                conditions
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <AddIcon /> Add Condition
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className='simple-form-id-panel'>
                                <label>Required:
                                    <input
                                        type="checkbox"
                                        checked={editingField.required === 'true'}
                                        onChange={(e) => {
                                            setEditingField({ ...editingField, required: e.target.checked ? 'true' : 'false' });
                                        }}
                                    />
                                </label>
                            </div>

                            <div className='simple-form-id-panel'>
                                <label>Class Name:</label>
                                <input
                                    type="text"
                                    value={editingField.className}
                                    onChange={(e) => setEditingField({ ...editingField, className: e.target.value })}
                                />
                            </div>

                            <div className='simple-form-id-panel'>
                                <label>Value:</label>
                                <input
                                    type="text"
                                    value={editingField.value}
                                    onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                                />
                            </div>

                            {['select', 'radio', 'checkbox'].includes(editingField.type) && (
                                <div>
                                    <h4>{editingField.type === 'select' ? 'Select Options' : 'Options'}</h4>
                                    {editingField.options.map((option, optionIndex) => (
                                        <div className='simple-form-id-panel select-fields-panel' key={optionIndex}>
                                            <label>Label:</label>
                                            <input
                                                type="text"
                                                value={option.label}
                                                onChange={(e) => {
                                                    const updatedOptions = [...editingField.options];
                                                    const newLabel = e.target.value;
                                                    const currentOption = updatedOptions[optionIndex];
                                                    const newValue = currentOption.value === currentOption.label ? newLabel : currentOption.value;

                                                    updatedOptions[optionIndex] = { ...currentOption, label: newLabel, value: newValue };
                                                    setEditingField({ ...editingField, options: updatedOptions });
                                                }}
                                            />
                                            <label>Value:</label>
                                            <input
                                                type="text"
                                                value={option.value}
                                                onChange={(e) => {
                                                    const updatedOptions = [...editingField.options];
                                                    updatedOptions[optionIndex] = { ...option, value: e.target.value };
                                                    setEditingField({ ...editingField, options: updatedOptions });
                                                }}
                                            />
                                            <button className='option-remover-multiple' onClick={() => handleRemoveOption(optionIndex)}>
                                                <CloseIcon className='add-options' />
                                            </button>
                                        </div>
                                    ))}
                                    <button className='jsonbtn' onClick={handleAddOption}><AddIcon /> Add Option</button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Preview Modal */}
                <PreviewModal
                    isOpen={isPreviewModalOpen}
                    toggleModal={togglePreviewModal}
                    formFields={formFields}
                    formStructure={formStructure}
                    rowLayoutMode={rowLayoutMode}
                />

                {createTableModal && (
                    <Modal>
                        <div
                            className="create-table-modal-wrap modal-content manage-modal-content"
                            ref={createTableModalRef}>
                            <div
                                className="cross_sign"
                                onClick={() => handleClosePopup()}
                            >
                                {Cross}
                            </div>
                            <pre>Form Data: {JSON.stringify(formData, null, 2)}</pre>
                        </div>
                    </Modal>
                )}
            </div>

            <NoticeModal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                onDecline={closeModal}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                confirmText={modalConfig.confirmText}
                declineText={modalConfig.declineText}
                position={modalConfig.position || 'center'}
                autoClose={modalConfig.type === 'toast'}
                autoCloseTime={3000}
            />
        </div>
    );
};

export default FormBuilder;